import { Box, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    useClinicalListsListPeople,
    usePatientsGetById,
} from '~/backend/connector-next-api/connector-next-components';
import {
    LabelText,
    labelValueGridSx,
    ValueText,
} from '~/components/Fields/LabeledValue';
import { useClinicalLists } from '~/containers/patient-list/hooks';
import {
    patientAssignee,
    patientCourseStagesDone,
    resolveCourseStage,
} from '~/containers/patient-list/model';
import {
    AssigneeAvatar,
    ConfigurationLoadFailure,
    CourseStageName,
    TagChips,
} from '~/containers/patient-list/shared';

/**
 * Where a connector patient's course stands, as the worklist records it: the
 * course stage with the stages done, the clinical tags and who holds the
 * patient. Read only. Each value reads "…" until the record has loaded, so no
 * stage is shown before the patient's own, and a record, tag list or user list
 * that cannot be read says so.
 */
export default function PatientCourseFacts({
    patientId,
}: {
    /** Connector patient row GUID. */
    patientId: string;
}) {
    const intl = useIntl();
    const { data: patient, isError } = usePatientsGetById({
        pathParams: { id: patientId },
    });
    const { data: lists } = useClinicalLists();
    const { data: people } = useClinicalListsListPeople({});

    if (!patient && isError) {
        return (
            <Typography variant="body2" color="error.main">
                <FormattedMessage id="patientList.drilldown.error" />
            </Typography>
        );
    }

    const tagIds = patient?.tagIds ?? [];
    const assignee = patient && patientAssignee(intl, people, patient);

    return (
        <Box sx={labelValueGridSx} data-testid="patient-course-facts">
            <LabelText>
                <FormattedMessage id="patientList.column.status" />
            </LabelText>
            {patient ? (
                <ValueText component="div">
                    <CourseStageName
                        stage={resolveCourseStage(patient.courseStage)}
                        done={patientCourseStagesDone(patient.courseStagesDone)}
                    />
                </ValueText>
            ) : (
                <ValueText>…</ValueText>
            )}

            <LabelText>
                <FormattedMessage id="patientList.tags.label" />
            </LabelText>
            {patient && tagIds.length > 0 ? (
                <TagChips tagIds={tagIds} tags={lists.tags} />
            ) : (
                <ValueText>{patient ? '—' : '…'}</ValueText>
            )}

            <LabelText>
                <FormattedMessage id="patientList.column.assignee" />
            </LabelText>
            {patient ? (
                <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                    <AssigneeAvatar assignee={assignee} />
                    <ValueText>
                        {assignee?.name ??
                            intl.formatMessage({
                                id: 'patientList.unassigned',
                            })}
                    </ValueText>
                </Box>
            ) : (
                <ValueText>…</ValueText>
            )}

            <ConfigurationLoadFailure sx={{ gridColumn: '1 / -1' }} />
        </Box>
    );
}
